import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useContent } from "../lib/content";
import { Phone, ChevronRight, Mail } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useContent();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOkMsg("");
    if (!email.trim()) {
      setErr("Zadajte prosím e-mailovú adresu.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setOkMsg(
        data?.message ||
          "Ak zadaný e-mail existuje, poslali sme link na obnovu hesla."
      );
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top utility bar */}
      <div className="topbar">
        <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Phone size={11} />
            <span>{t("topbar_phone_label", "VOLAJTE")}: {t("topbar_phone", "+421 900 000 000")}</span>
          </div>
          <nav className="flex items-center gap-1 text-[11px] uppercase">
            <Link to="/" className="px-2 py-1 hover:text-[color:var(--brand)]">Domov</Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <Link to="/login" className="px-2 py-1 hover:text-[color:var(--brand)]">Prihlásenie</Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <span className="px-2 py-1 font-bold" style={{ color: "var(--brand)" }}>Zabudnuté heslo</span>
          </nav>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <div
              className="w-14 h-14 flex items-center justify-center rounded shrink-0"
              style={{ background: "var(--text)", color: "#fff", fontWeight: 900, fontSize: 24, letterSpacing: "-0.04em" }}
            >
              {t("header_logo_initials", "PS")}
            </div>
            <div className="leading-tight">
              <div className="font-bold text-xl" style={{ color: "var(--text)" }}>{t("header_company_name", "PROPARTS.SK")}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>{t("header_company_subtitle", "OEM diely · Veľkoobchod")}</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/">Domov</Link> / <Link to="/login">Prihlásenie</Link> / <span style={{ color: "var(--text)" }}>Zabudnuté heslo</span>
        </div>
      </div>

      <main className="flex-1 bg-[color:var(--surface-elev,#f5f5f5)]">
        <div className="max-w-[700px] w-full mx-auto px-4 py-10">
          <h1 className="text-2xl font-bold mb-3 uppercase tracking-wide" style={{ color: "var(--text)" }}>
            Zabudnuté heslo
          </h1>
          <div className="border-b-2 mb-6" style={{ borderColor: "var(--brand)" }} />

          <div className="card-elev">
            <div className="section-title">Žiadosť o obnovu hesla</div>
            <div className="p-6">
              <p className="text-sm mb-5" style={{ color: "var(--text-mute)" }}>
                Zadajte e-mail použitý pri registrácii. Ak účet existuje,
                pošleme vám link, ktorým si môžete nastaviť nové heslo. Link je
                <strong> platný 1 hodinu</strong>.
              </p>

              {okMsg ? (
                <div
                  className="text-sm py-3 px-4 mb-4 flex items-start gap-3"
                  style={{ background: "#EFFAE9", color: "#2C6E1F", border: "1px solid #C9E5B7", borderRadius: 4 }}
                  data-testid="forgot-success"
                >
                  <Mail size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Úspešná žiadosť.</strong>
                    <div className="mt-1">{okMsg}</div>
                    <div className="mt-2 text-xs" style={{ color: "var(--text-mute)" }}>
                      Ak e-mail nedorazí do niekoľkých minút, skontrolujte aj priečinok SPAM.
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 sm:items-center">
                    <label className="text-sm font-bold">
                      E-mailová adresa: <span style={{ color: "var(--out-stock)" }}>*</span>
                    </label>
                    <input
                      type="email"
                      className="input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      data-testid="forgot-email-input"
                    />
                  </div>

                  {err && (
                    <div
                      className="text-sm py-2 px-3"
                      style={{ background: "#FEE", color: "var(--out-stock)", border: "1px solid #FCC", borderRadius: 4 }}
                      data-testid="forgot-error"
                    >
                      <strong>CHYBA:</strong> {err}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-center pt-2">
                    <span />
                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary"
                        data-testid="forgot-submit-button"
                      >
                        {loading ? "Moment..." : "Odoslať obnovovací link"}
                      </button>
                      <Link
                        to="/login"
                        className="text-xs hover:underline"
                        style={{ color: "var(--brand)" }}
                        data-testid="forgot-back-link"
                      >
                        ← Späť na prihlásenie
                      </Link>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="mt-10 border-t" style={{ background: "var(--surface-elev)", borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 text-xs flex flex-wrap items-center justify-between gap-3" style={{ color: "var(--text-mute)" }}>
          <div>{t("footer_text", "PROPARTS.SK — OEM Veľkoobchod dielov")} · © {new Date().getFullYear()}</div>
          <div>{t("footer_links", "Prihlásenie · Nákupný košík · Kontakt")}</div>
        </div>
      </footer>
    </div>
  );
}
